import React from 'react';
import {
  Button,
  Card,
  EmptyState,
  Flex,
  LoadingOrbit,
  Markdown,
  Page,
  PillToggle,
  Text,
  useToast,
} from '@chatsdet/ui';
import { ArrowLeft, FileText, LinkSimple } from '@phosphor-icons/react';
import { useNavigate, useParams } from '@tanstack/react-router';
import { useAuth } from '../../contexts/AuthContext';
import { useBreadcrumbs } from '../../contexts/BreadcrumbsContext';
import { useUtilityGist } from '../../features/utilities/api';

/** Org-scoped gist detail at /utilities/gists/$id — the deep-linkable viewer. */
export function UtilityGist() {
  const { id } = useParams({ from: '/utilities/gists/$id' });
  const { setBreadcrumbs } = useBreadcrumbs();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const { data, isLoading, isError } = useUtilityGist(id);
  const gist = data?.data;
  const [mode, setMode] = React.useState<'rendered' | 'plain'>('rendered');

  // Utilities is not GA — bounce out if the org's flag is off.
  React.useEffect(() => {
    if (user && !user.features?.utilities) {
      void navigate({ to: '/' });
    }
  }, [user, navigate]);

  React.useEffect(() => {
    setBreadcrumbs([
      { label: 'Utilities', href: '/utilities' },
      { label: gist?.title ?? 'Gist' },
    ]);
  }, [setBreadcrumbs, gist?.title]);

  React.useEffect(() => {
    if (gist) setMode(gist.format === 'markdown' ? 'rendered' : 'plain');
  }, [gist]);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success('Link copied');
    } catch {
      toast.error('Failed to copy link');
    }
  };

  const backButton = (
    <Button variant="ghost" onPress={() => void navigate({ to: '/utilities' })}>
      <ArrowLeft size={16} /> Back
    </Button>
  );

  if (isError) {
    return (
      <Page>
        <Flex direction="column" gap="md">
          <Flex>{backButton}</Flex>
          <EmptyState
            icon={<FileText size={32} />}
            title="Gist not found"
            description="It may have been deleted, or the link belongs to a different organization."
          />
        </Flex>
      </Page>
    );
  }

  if (isLoading || !gist) {
    return (
      <Page>
        <LoadingOrbit />
      </Page>
    );
  }

  return (
    <Page>
      <Flex direction="column" gap="md">
        <Flex align="center" justify="between">
          {backButton}
          <Flex align="center" gap="xs">
            <Button variant="ghost" size="sm" onPress={copyLink}>
              <LinkSimple size={16} /> Copy link
            </Button>
            <PillToggle
              value={mode}
              onChange={(v) => setMode(v as 'rendered' | 'plain')}
              options={[
                { value: 'rendered', label: 'Markdown' },
                { value: 'plain', label: 'Plain' },
              ]}
            />
          </Flex>
        </Flex>
        <div>
          <Text weight="bold" className="text-lg">
            {gist.title}
          </Text>
          <Text className="text-muted-foreground text-sm">
            {gist.visibility} · {gist.format}
            {gist.viewUrl ? ` · ${gist.viewUrl}` : ''}
          </Text>
        </div>
        <Card className="p-4">
          {mode === 'rendered' ? (
            <Markdown className="prose prose-sm max-w-none text-foreground">
              {gist.content}
            </Markdown>
          ) : (
            <pre className="whitespace-pre-wrap break-words text-sm font-mono">{gist.content}</pre>
          )}
        </Card>
      </Flex>
    </Page>
  );
}
